import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
import { PromotionTrackingContext } from '@lib/PromotionTrackingContext';
import { useGTMDataLayer } from './GTM/useGTMDataLayer';
import { createPromotionEventPayload } from './GTM/useSendGTMEventOnce';
import { queueGTMEvent } from './GTM/queueGTMEvent';
import { NAFGTMEvent } from '../../../types/GTM/NAFGTMEvent';
import { useInView } from './useInView';

type PromotionItem = {
  item_id?: string;
  item_name?: string;
  promotion_id?: string;
  promotion_name?: string;
  creative_slot: string;
  creative_name: string;
  disabled?: boolean;
};

const SELECTED_PROMOTION_SESSION_KEY = 'selectedPromotion';
const SELECTED_PROMOTION_FALLBACK_KEY = 'selectedPromotionFallback';
const SELECTED_PROMOTION_FALLBACK_MAX_AGE_MS = 1000 * 60 * 60; // 1 hour

type StoredPromotionItem = {
  promotion_id?: string;
  promotion_name?: string;
  creative_slot?: string;
  creative_name?: string;
  storedAt?: number;
};

const parsePromotionData = (
  storedPromotionDataAsString: string | null,
  options?: { requireFreshTimestamp?: boolean },
) => {
  if (!storedPromotionDataAsString || storedPromotionDataAsString.length === 0) {
    return null;
  }

  try {
    const storedPromotion = JSON.parse(storedPromotionDataAsString) as StoredPromotionItem;
    const creativeSlot = storedPromotion.creative_slot;
    const promotionId = storedPromotion.promotion_id;
    const promotionName = storedPromotion.promotion_name;
    const creativeName = storedPromotion.creative_name;

    if (!creativeSlot || !promotionId || !promotionName || !creativeName) {
      return null;
    }

    if (options?.requireFreshTimestamp) {
      const { storedAt } = storedPromotion;
      const hasValidTimestamp = typeof storedAt === 'number' && Number.isFinite(storedAt);
      if (!hasValidTimestamp || Date.now() - storedAt > SELECTED_PROMOTION_FALLBACK_MAX_AGE_MS) {
        return null;
      }
    }

    return {
      creative_slot: creativeSlot,
      creative_name: creativeName,
      promotion_id: promotionId,
      promotion_name: promotionName,
    };
  } catch (e) {
    return null;
  }
};

const createStoredPromotionData = (
  promotionData: NonNullable<ReturnType<typeof parsePromotionData>>,
): StoredPromotionItem => ({
  ...promotionData,
  storedAt: Date.now(),
});

const storePromotionData = (promotionData: NonNullable<ReturnType<typeof parsePromotionData>>) => {
  const stringifiedPromotionData = JSON.stringify(createStoredPromotionData(promotionData));

  try {
    window.sessionStorage.setItem(SELECTED_PROMOTION_SESSION_KEY, stringifiedPromotionData);
    // Fallback storage for flows where receipt opens in a new tab/context after external payment.
    window.localStorage.setItem(SELECTED_PROMOTION_FALLBACK_KEY, stringifiedPromotionData);
    // eslint-disable-next-line no-empty
  } catch (_) {}
};

export const useTrackPromotion = ({
  promotion_id = 'bli-medlem',
  promotion_name = 'Bli medlem',
  creative_slot,
  creative_name,
  disabled = false,
}: PromotionItem) => {
  const dataLayer = useGTMDataLayer();
  const location = useLocation();
  const promotionTracking = useContext(PromotionTrackingContext);
  const virtualPageViewTrackedState = promotionTracking?.isVirtualPageViewTracked;
  const isVirtualPageViewTrackedRef = promotionTracking?.isVirtualPageViewTrackedRef;
  const pageViewKeyRef = promotionTracking?.pageViewKeyRef;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(wrapperRef, { triggerOnMount: true });
  const lastTrackedKeyRef = useRef<string | null>(null);
  const pendingViewPromotionRef = useRef<{
    key: string;
    event: ReturnType<typeof createPromotionEventPayload>;
  } | null>(null);

  const creativeSlotWithPath = useMemo(() => {
    const path = location.pathname;
    if (!path) {
      return creative_slot;
    }
    if (!creative_slot) {
      return path;
    }
    if (creative_slot.includes(path)) {
      return creative_slot;
    }
    return `${path} | ${creative_slot}`;
  }, [creative_slot, location.pathname]);

  const promotionItem = useMemo(
    () => ({
      promotion_id,
      promotion_name,
      creative_slot: creativeSlotWithPath,
      creative_name,
    }),
    [promotion_id, promotion_name, creativeSlotWithPath, creative_name],
  );

  const viewPromotionEvent = useMemo(() => {
    if (!dataLayer || !isInView) {
      return undefined;
    }

    return createPromotionEventPayload({
      event: NAFGTMEvent.viewPromotion,
      ecommerce: {
        items: [promotionItem],
      },
    });
  }, [dataLayer, isInView, promotionItem]);

  const locationKey = useMemo(() => {
    if (location.key) {
      return location.key;
    }

    return `${location.pathname}${location.search}${location.hash}`;
  }, [location.key, location.pathname, location.search, location.hash]);

  const pageViewLocationKey = useMemo(
    () => `${location.pathname}${location.search}`,
    [location.pathname, location.search],
  );

  const isExcludedPath = useMemo(
    () =>
      location.pathname === '/medlemskap' ||
      location.pathname === '/medlemskap-og-priser' ||
      location.pathname === '/bli-medlem',
    [location.pathname],
  );
  const isExcludedLocation = disabled || isExcludedPath || Boolean(promotionTracking?.disablePromotionTracking);

  const viewPromotionKey = useMemo(
    () => `${locationKey}:${promotion_id || ''}:${promotion_name || ''}:${creativeSlotWithPath}:${creative_name}`,
    [locationKey, promotion_id, promotion_name, creativeSlotWithPath, creative_name],
  );

  useEffect(() => {
    if (!viewPromotionEvent || !dataLayer || isExcludedLocation) {
      return;
    }

    const isVirtualPageViewTracked = isVirtualPageViewTrackedRef?.current ?? true;
    const hasMatchingPageViewKey = pageViewKeyRef ? pageViewKeyRef.current === pageViewLocationKey : true;
    const isPageViewReady = isVirtualPageViewTracked && hasMatchingPageViewKey;

    if (!isPageViewReady) {
      pendingViewPromotionRef.current = {
        key: viewPromotionKey,
        event: viewPromotionEvent,
      };
      return;
    }

    if (lastTrackedKeyRef.current === viewPromotionKey) {
      return;
    }

    lastTrackedKeyRef.current = viewPromotionKey;
    queueGTMEvent(dataLayer, viewPromotionEvent);
  }, [
    dataLayer,
    viewPromotionEvent,
    viewPromotionKey,
    isExcludedLocation,
    pageViewLocationKey,
    isVirtualPageViewTrackedRef,
    pageViewKeyRef,
    virtualPageViewTrackedState,
  ]);

  useEffect(() => {
    if (!dataLayer || isExcludedLocation) {
      return;
    }

    const isVirtualPageViewTracked = isVirtualPageViewTrackedRef?.current ?? true;
    const hasMatchingPageViewKey = pageViewKeyRef ? pageViewKeyRef.current === pageViewLocationKey : true;
    const isPageViewReady = isVirtualPageViewTracked && hasMatchingPageViewKey;

    if (!isPageViewReady) {
      return;
    }

    const pending = pendingViewPromotionRef.current;
    if (!pending) {
      return;
    }

    if (lastTrackedKeyRef.current === pending.key) {
      pendingViewPromotionRef.current = null;
      return;
    }

    lastTrackedKeyRef.current = pending.key;
    pendingViewPromotionRef.current = null;
    queueGTMEvent(dataLayer, pending.event);
  }, [
    dataLayer,
    isExcludedLocation,
    pageViewLocationKey,
    isVirtualPageViewTrackedRef,
    pageViewKeyRef,
    virtualPageViewTrackedState,
  ]);

  const trackSelectPromotion = useCallback(() => {
    if (!dataLayer || isExcludedLocation) {
      return;
    }

    const selectPromotionEvent = createPromotionEventPayload({
      event: NAFGTMEvent.selectPromotion,
      ecommerce: {
        items: [promotionItem],
      },
    });

    dataLayer.push(selectPromotionEvent);

    if (typeof window !== 'undefined') {
      try {
        storePromotionData(promotionItem);
        // eslint-disable-next-line no-empty
      } catch (_) {}
    }
  }, [dataLayer, promotionItem, isExcludedLocation]);

  return { wrapperRef, trackSelectPromotion };
};

export const getPromotionDataFromSessionStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  let storedPromotionDataAsString: string | null = null;
  try {
    storedPromotionDataAsString = window.sessionStorage?.getItem(SELECTED_PROMOTION_SESSION_KEY) ?? null;
  } catch (error) {
    storedPromotionDataAsString = null;
  }

  const promotionDataFromSessionStorage = parsePromotionData(storedPromotionDataAsString);
  if (promotionDataFromSessionStorage) {
    return promotionDataFromSessionStorage;
  }

  let storedFallbackPromotionDataAsString: string | null = null;
  try {
    storedFallbackPromotionDataAsString = window.localStorage?.getItem(SELECTED_PROMOTION_FALLBACK_KEY) ?? null;
  } catch (error) {
    return null;
  }

  const promotionDataFromLocalStorageFallback = parsePromotionData(storedFallbackPromotionDataAsString, {
    requireFreshTimestamp: true,
  });

  if (promotionDataFromLocalStorageFallback) {
    storePromotionData(promotionDataFromLocalStorageFallback);
    return promotionDataFromLocalStorageFallback;
  }

  if (storedFallbackPromotionDataAsString) {
    try {
      window.localStorage?.removeItem(SELECTED_PROMOTION_FALLBACK_KEY);
      // eslint-disable-next-line no-empty
    } catch (_) {}
  }

  return null;
};
